import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import AirOutlinedIcon from '@mui/icons-material/AirOutlined';
import { Box, Menu, MenuItem, Switch, Typography } from '@mui/material';

import type { DetailedCircuitGeometry, TrackContributionGpsCoord } from '@kangaroo/shared';
import {
  analyzeBrakingZones,
  analyzeCoastingZones,
  isContinuousTelemetryPair,
  smoothTelemetryArc,
} from '@kangaroo/shared';

import {
  getGoogleMapsApiKey,
  getMapboxAccessToken,
  mapboxMissingKeyHint,
} from '../../lib/runtimeConfig';
import type { ReferenceTrackGuideColors } from '../../lib/osmTrackOverlay';
import {
  loadGoogleMaps,
  mapsAuthHasFailed,
  mapsDevAuthHint,
  mapsMissingKeyHint,
  onMapsAuthFailure,
  registerGoogleMapsAuthFailureHandler,
  resetMapsAuthFailure,
} from '../../lib/mapsLoader';
import {
  interpolateMapPointAtTime,
  trackSpeedRange,
  type TrackMapChannelOverlay,
  type MapReplayCursor,
  type TrackMapMarker,
  type TrackMapPoint,
} from '../../lib/telemetrySession';
import { GoogleMapsTrackMap } from './GoogleMapsTrackMap';
import { MapboxTrackMap } from './MapboxTrackMap';
import { AppleMapsTrackMap } from './AppleMapsTrackMap';
import { TrackMapLegend, type TrackMapLegendItem } from './TrackMapLegend';
import { loadMapKit, mapkitAuthHasFailed, mapkitMissingConfigHint } from '../../lib/mapkitLoader';
import {
  buildPrecisionTrackSegments as trackSegments,
  fitPrecisionTrackViewport,
  precisionTrackBounds,
} from '../../lib/precisionTrackMap';

const VIEWBOX_WIDTH = 640;
const VIEWBOX_HEIGHT = 280;

type SpeedRange = {
  min: number;
  max: number;
};

type GpsSpeedTrackMapProps = {
  points: TrackMapPoint[];
  trackGroups?: TrackMapPoint[][];
  referenceTrackGroups?: TrackMapPoint[][];
  speedRange?: SpeedRange;
  idealPoints?: TrackMapPoint[];
  showIdealLine?: boolean;
  showLapLines?: boolean;
  channelOverlays?: TrackMapChannelOverlay[];
  height?: number;
  title?: string;
  showLegend?: boolean;
  showBrakingSpots?: boolean;
  showBrakingToggle?: boolean;
  brakingOverlayEnabled?: boolean;
  onBrakingOverlayEnabledChange?: (enabled: boolean) => void;
  showCoastingSpots?: boolean;
  coastingOverlayEnabled?: boolean;
  onCoastingOverlayEnabledChange?: (enabled: boolean) => void;
  legendTrackItems?: TrackMapLegendItem[];
  fill?: boolean;
  cursorTimecodeMs?: number | null;
  replayCursors?: MapReplayCursor[];
  followReplayCursor?: boolean;
  resolveReplayCursorTimecodeMsRef?: MutableRefObject<
    ((cursor: MapReplayCursor) => number | null) | undefined
  >;
  resetViewKey?: string | number;
  onMapClick?: () => void;
  onMapLatLngClick?: (coord: { lat: number; lon: number }) => void;
  onMapLatLngHover?: (coord: { lat: number; lon: number } | null) => void;
  crosshairCursor?: boolean;
  freehandDrawActive?: boolean;
  onMapLatLngDrawStroke?: (coord: { lat: number; lon: number }) => void;
  onMapLatLngDrawEnd?: () => void;
  highlightLines?: Array<{
    id: string;
    points: Array<{ lat: number; lon: number }>;
    color?: string;
    width?: number;
    opacity?: number;
    dash?: number[];
  }>;
  detailedCircuitPreview?: DetailedCircuitGeometry | null;
  selectedDetailedFeatureId?: string;
  persistentDetailedFeatureIds?: string[];
  interactiveSectionDividers?: boolean;
  selectedSectionBoundaryLine?: {
    pointA: { lat: number; lon: number };
    pointB: { lat: number; lon: number };
  } | null;
  corridorOutlineRings?: TrackContributionGpsCoord[][];
  onMapMarkerClick?: (markerId: string) => void;
  onMapMarkerDragEnd?: (markerId: string, coord: { lat: number; lon: number }) => void;
  onOverlaysReady?: () => void;
  mapHoverMarker?: { lat: number; lon: number };
  startFinishLine?: { pointA: { lat: number; lon: number }; pointB: { lat: number; lon: number } };
  crossTrackLines?: Array<{
    id: string;
    pointA: { lat: number; lon: number };
    pointB: { lat: number; lon: number };
    color?: string;
    draggable?: boolean;
    selected?: boolean;
    label?: string;
    lineStyle?: 'pill' | 'catalog';
  }>;
  mapMarkers?: TrackMapMarker[];
  mapMarkerScale?: number;
  mapMarkerLightOutline?: boolean;
  heatLineWidth?: number;
  heatLineHaloWidth?: number;
  heatLineWhiteHalo?: boolean;
  fitBoundsPadding?: number;
  maxFitZoom?: number;
  minFitZoom?: number;
  sectorHighlightGroups?: TrackMapPoint[][];
  selectedSectorIndex?: number | null;
  mapsDevHint?: string | null;
  /** When true, SVG track draws over satellite with a transparent canvas. */
  transparentBackground?: boolean;
  /** When true, skip SVG track polylines — Google Maps renders them instead. */
  hideTrackGraphics?: boolean;
  /** Lighter dashed styling for imported OSM / reference outlines. */
  referenceTrackStyle?: 'default' | 'guide';
  referenceTrackGuideColors?: ReferenceTrackGuideColors[];
  referenceTrackGuideArrows?: boolean;
  /** Center the map when no GPS trace is available (manual layout mode). */
  mapViewport?: { lat: number; lon: number; zoom?: number };
  /** When false, the map keeps the user's pan/zoom while overlays update. */
  autoFitBounds?: boolean;
  /** When false, location search will not recenter the map (use while outlining). */
  followMapViewport?: boolean;
  /** Satellite basemap provider. Create Track uses Mapbox for better rural coverage. */
  basemapProvider?: 'google' | 'apple' | 'mapbox';
  /** When false, render the GPS graphics on the plain SVG canvas without map tiles. */
  showBasemap?: boolean;
  /** Background used by the plain SVG canvas when no basemap is shown. */
  svgBackgroundColor?: string;
  /** Use narrower track strokes for a precision, line-only presentation. */
  fineTrackLines?: boolean;
};

function hasGeoPoints(points: TrackMapPoint[]) {
  return (
    points.length > 0 &&
    points.every((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon))
  );
}

function svgIdPart(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function nearestTrackCoordFromSvgPoint(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
  points: TrackMapPoint[],
): { lat: number; lon: number } | null {
  if (points.length === 0) {
    return null;
  }

  const clickPoint = svg.createSVGPoint();
  clickPoint.x = clientX;
  clickPoint.y = clientY;
  const inverse = svg.getScreenCTM()?.inverse();
  if (!inverse) {
    return null;
  }

  const mapped = clickPoint.matrixTransform(inverse);
  let nearest = points[0]!;
  let nearestDist = Number.POSITIVE_INFINITY;
  for (const trackPoint of points) {
    const dx = trackPoint.x - mapped.x;
    const dy = trackPoint.y - mapped.y;
    const dist = dx * dx + dy * dy;
    if (dist < nearestDist) {
      nearest = trackPoint;
      nearestDist = dist;
    }
  }

  return { lat: nearest.lat, lon: nearest.lon };
}

function projectCoordOnTrack(points: TrackMapPoint[], coord: { lat: number; lon: number }) {
  if (points.length === 0) {
    return null;
  }

  let nearest = points[0]!;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const point of points) {
    const distance = (point.lat - coord.lat) ** 2 + (point.lon - coord.lon) ** 2;
    if (distance < bestDistance) {
      bestDistance = distance;
      nearest = point;
    }
  }

  return nearest;
}

function SvgReplayCursorLayer({
  cursors,
  resolveTimecodeMsRef,
  compact,
  scale = 1,
  onFollowPoint,
}: {
  cursors: MapReplayCursor[];
  resolveTimecodeMsRef?: MutableRefObject<
    ((cursor: MapReplayCursor) => number | null) | undefined
  >;
  compact: boolean;
  scale?: number;
  onFollowPoint?: (
    point: NonNullable<ReturnType<typeof interpolateMapPointAtTime>>
  ) => void;
}) {
  const [liveTimecodes, setLiveTimecodes] = useState<Record<string, number | null>>({});

  useEffect(() => {
    if (!resolveTimecodeMsRef || cursors.length === 0) return;

    let animationFrame = 0;
    let lastSignature = '';
    const update = () => {
      const resolve = resolveTimecodeMsRef.current;
      if (resolve) {
        const next = Object.fromEntries(cursors.map((cursor) => [cursor.id, resolve(cursor)]));
        const signature = cursors.map((cursor) => `${cursor.id}:${next[cursor.id] ?? 'null'}`).join('|');
        if (signature !== lastSignature) {
          lastSignature = signature;
          setLiveTimecodes(next);
          const followCursor = cursors.find((cursor) => cursor.follow);
          const followTimecodeMs = followCursor ? next[followCursor.id] : null;
          if (followCursor && followTimecodeMs !== null && followTimecodeMs !== undefined) {
            const followPoint = interpolateMapPointAtTime(followCursor.points, followTimecodeMs);
            if (followPoint) onFollowPoint?.(followPoint);
          }
        }
      }
      animationFrame = window.requestAnimationFrame(update);
    };

    animationFrame = window.requestAnimationFrame(update);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [cursors, onFollowPoint, resolveTimecodeMsRef]);

  const cursorPoints = cursors
    .map((cursor) => {
      const resolvedTimecodeMs = Object.prototype.hasOwnProperty.call(liveTimecodes, cursor.id)
        ? liveTimecodes[cursor.id]
        : cursor.timecodeMs;
      if (resolvedTimecodeMs === null || resolvedTimecodeMs === undefined) return null;
      const point = interpolateMapPointAtTime(cursor.points, resolvedTimecodeMs);
      return point ? { ...cursor, point } : null;
    })
    .filter(
      (
        entry,
      ): entry is MapReplayCursor & {
        point: NonNullable<ReturnType<typeof interpolateMapPointAtTime>>;
      } => entry !== null,
    );

  const compactScale = compact ? scale : 1;
  const haloRadius = (compact ? 12.75 : 14) * compactScale;
  const avatarHaloRadius = (compact ? 14 : 17) * compactScale;
  const cursorRadius = (compact ? 10 : 12) * compactScale;
  const avatarInset = (compact ? 10 : 12) * compactScale;
  const strokeWidth = (compact ? 2.25 : 2.8) * compactScale;

  return (
    <>
      <defs>
        {cursorPoints
          .filter((cursor) => Boolean(cursor.avatarUrl))
          .map((cursor) => (
            <clipPath key={`cursor-clip-${cursor.id}`} id={`cursor-clip-${svgIdPart(cursor.id)}`}>
              <circle cx={cursor.point.x} cy={cursor.point.y} r={cursorRadius} />
            </clipPath>
          ))}
      </defs>
      {cursorPoints.map((cursor) => {
        const compactLabel = [cursor.label, cursor.initials].filter(Boolean).join(' · ');
        const compactLabelFontSize = 21 * compactScale;
        const compactLabelHeight = 31.5 * compactScale;
        const compactLabelWidth = Math.max(
          46 * compactScale,
          compactLabel.length * compactLabelFontSize * 0.62 + 20 * compactScale,
        );
        const compactLabelX = cursor.point.x + cursorRadius + 2.5 * compactScale;
        return (
        <g key={cursor.id} data-testid="gps-replay-cursor">
          <circle
            data-testid="gps-replay-cursor-halo"
            cx={cursor.point.x}
            cy={cursor.point.y}
            r={cursor.avatarUrl ? avatarHaloRadius : haloRadius}
            fill={cursor.color}
            opacity="0.22"
          />
          {cursor.avatarUrl ? (
            <>
              <circle
                cx={cursor.point.x}
                cy={cursor.point.y}
                r={(compact ? 11 : 13.5) * compactScale}
                fill="#020A12"
                opacity="0.88"
              />
              <image
                href={cursor.avatarUrl}
                x={cursor.point.x - avatarInset}
                y={cursor.point.y - avatarInset}
                width={avatarInset * 2}
                height={avatarInset * 2}
                preserveAspectRatio="xMidYMid slice"
                clipPath={`url(#cursor-clip-${svgIdPart(cursor.id)})`}
              />
              <circle
                cx={cursor.point.x}
                cy={cursor.point.y}
                r={cursorRadius}
                fill="none"
                stroke={cursor.color}
                strokeWidth={strokeWidth}
                strokeDasharray={cursor.identityVariant ? '4 2.5' : undefined}
              />
            </>
          ) : (
            <>
              <circle
                cx={cursor.point.x}
                cy={cursor.point.y}
                r={cursorRadius}
                fill={compact ? cursor.color : 'rgba(2,10,18,0.88)'}
                stroke={cursor.color}
                strokeWidth={strokeWidth}
                strokeDasharray={cursor.identityVariant ? '4 2.5' : undefined}
              />
              {!compact ? (
                <text
                  x={cursor.point.x}
                  y={cursor.point.y + 3.2}
                  textAnchor="middle"
                  fontSize="8"
                  fontWeight="900"
                  fill="#FFFFFF"
                  paintOrder="stroke"
                  stroke="rgba(2,10,18,0.8)"
                  strokeWidth="1.1"
                >
                  {cursor.initials ?? '??'}
                </text>
              ) : null}
            </>
          )}
          {compact && compactLabel ? (
            <g pointerEvents="none">
              <rect
                x={compactLabelX}
                y={cursor.point.y - compactLabelHeight / 2}
                width={compactLabelWidth}
                height={compactLabelHeight}
                rx={compactLabelHeight / 2}
                fill="rgba(2,10,18,0.86)"
                stroke={cursor.color}
                strokeWidth={0.65 * compactScale}
              />
              <text
                x={compactLabelX + compactLabelWidth / 2}
                y={cursor.point.y + compactLabelFontSize * 0.34}
                textAnchor="middle"
                fontSize={compactLabelFontSize}
                fontWeight="800"
                fill="#F8FAFC"
              >
                {compactLabel}
              </text>
            </g>
          ) : null}
        </g>
        );
      })}
    </>
  );
}

function SvgGpsSpeedTrackMap({
  points,
  trackGroups,
  referenceTrackGroups = [],
  speedRange,
  idealPoints = [],
  showIdealLine = false,
  showLapLines = true,
  channelOverlays = [],
  height = 292,
  title,
  showLegend = true,
  legendTrackItems = [],
  fill = false,
  cursorTimecodeMs = null,
  replayCursors,
  resolveReplayCursorTimecodeMsRef,
  followReplayCursor = false,
  resetViewKey,
  onMapClick,
  onMapLatLngClick,
  onMapLatLngHover,
  crosshairCursor = false,
  highlightLines = [],
  onMapMarkerClick,
  onMapMarkerDragEnd,
  mapHoverMarker,
  startFinishLine,
  crossTrackLines = [],
  mapMarkers = [],
  mapMarkerScale = 1,
  sectorHighlightGroups = [],
  selectedSectorIndex = null,
  mapsDevHint = null,
  transparentBackground = false,
  svgBackgroundColor,
  fineTrackLines = false,
  hideTrackGraphics = false,
}: GpsSpeedTrackMapProps) {
  const [draggingMarkerId, setDraggingMarkerId] = useState<string | null>(null);
  const [panning, setPanning] = useState(false);
  const [containerAspect, setContainerAspect] = useState(VIEWBOX_WIDTH / VIEWBOX_HEIGHT);
  const [containerWidth, setContainerWidth] = useState(VIEWBOX_WIDTH);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const panStartRef = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    centerX: number;
    centerY: number;
  } | null>(null);
  const groups = trackGroups?.length ? trackGroups : points.length >= 2 ? [points] : [];
  const referenceGroups = referenceTrackGroups.filter((group) => group.length >= 2);
  const referenceSegments = referenceGroups.map((group) =>
    trackSegments(group, { min: 70, max: 75 }),
  );
  const segmentGroups = groups.map((group) =>
    trackSegments(group, speedRange ?? trackSpeedRange(group) ?? undefined),
  );
  const idealSegments = showIdealLine ? trackSegments(idealPoints, speedRange) : [];
  const start = points[0];
  const finish = points.at(-1);
  const activeCursors: MapReplayCursor[] = replayCursors?.length
    ? replayCursors
    : cursorTimecodeMs !== null && points.length >= 2
      ? [{ id: 'primary', points, timecodeMs: cursorTimecodeMs, color: '#A855F7', follow: true }]
      : [];
  const legendHeight = showLegend ? 28 : 0;
  const titleHeight = title ? 34 : 0;
  const trackBounds = useMemo(
    () => precisionTrackBounds(trackGroups?.length ? trackGroups.flat() : points),
    [points, trackGroups],
  );
  const fittedViewport = useMemo(
    () => fitPrecisionTrackViewport(trackBounds, containerAspect, fineTrackLines),
    [containerAspect, fineTrackLines, trackBounds],
  );
  const [viewport, setViewport] = useState(() => ({
    centerX: VIEWBOX_WIDTH / 2,
    centerY: VIEWBOX_HEIGHT / 2,
    zoom: 1,
  }));
  const viewWidth = fineTrackLines ? fittedViewport.width / viewport.zoom : VIEWBOX_WIDTH;
  const viewHeight = fineTrackLines ? fittedViewport.height / viewport.zoom : VIEWBOX_HEIGHT;
  const viewCenterX = fineTrackLines ? viewport.centerX : VIEWBOX_WIDTH / 2;
  const viewCenterY = fineTrackLines ? viewport.centerY : VIEWBOX_HEIGHT / 2;
  const activeViewBox = `${viewCenterX - viewWidth / 2} ${viewCenterY - viewHeight / 2} ${viewWidth} ${viewHeight}`;
  const precisionScreenScale = fineTrackLines
    ? Math.max(0.08, (fittedViewport.width / containerWidth / viewport.zoom) * 0.55)
    : 1;
  const renderedMapMarkerScale = mapMarkerScale * precisionScreenScale;
  const replayCursorScale = fineTrackLines
    ? Math.max(0.08, fittedViewport.width / containerWidth / viewport.zoom)
    : 1;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || typeof ResizeObserver === 'undefined') return;
    const updateAspect = () => {
      const bounds = svg.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0) {
        setContainerAspect(bounds.width / bounds.height);
        setContainerWidth(bounds.width);
      }
    };
    updateAspect();
    const observer = new ResizeObserver(updateAspect);
    observer.observe(svg);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setViewport({
      centerX: fittedViewport.centerX,
      centerY: fittedViewport.centerY,
      zoom: 1,
    });
  }, [fineTrackLines, fittedViewport.centerX, fittedViewport.centerY, resetViewKey]);

  const resetPrecisionViewport = useCallback(() => {
    setViewport({ centerX: fittedViewport.centerX, centerY: fittedViewport.centerY, zoom: 1 });
  }, [fittedViewport.centerX, fittedViewport.centerY]);
  const followPrecisionReplayCursor = useCallback(
    (point: NonNullable<ReturnType<typeof interpolateMapPointAtTime>>) => {
      if (!fineTrackLines || !followReplayCursor) return;
      const followZoom = 3;
      const followWidth = fittedViewport.width / followZoom;
      const followHeight = fittedViewport.height / followZoom;
      svgRef.current?.setAttribute(
        'viewBox',
        `${point.x - followWidth / 2} ${point.y - followHeight * 0.42} ${followWidth} ${followHeight}`,
      );
    },
    [fineTrackLines, fittedViewport.height, fittedViewport.width, followReplayCursor],
  );

  useEffect(() => {
    if (fineTrackLines && !followReplayCursor) resetPrecisionViewport();
  }, [fineTrackLines, followReplayCursor, resetPrecisionViewport]);

  const handlePrecisionWheel = useCallback(
    (event: WheelEvent) => {
      if (!fineTrackLines) return;
      event.preventDefault();
      event.stopPropagation();
      const svg = svgRef.current;
      if (!svg) return;
      const transform = svg.getScreenCTM();
      if (!transform) return;
      const anchor = new DOMPoint(event.clientX, event.clientY).matrixTransform(transform.inverse());
      const zoomFactor = Math.exp(-event.deltaY * 0.0015);
      setViewport((current) => {
        const nextZoom = Math.min(8, Math.max(1, current.zoom * zoomFactor));
        if (nextZoom === current.zoom) return current;
        const currentWidth = fittedViewport.width / current.zoom;
        const currentHeight = fittedViewport.height / current.zoom;
        const nextWidth = fittedViewport.width / nextZoom;
        const nextHeight = fittedViewport.height / nextZoom;
        const currentLeft = current.centerX - currentWidth / 2;
        const currentTop = current.centerY - currentHeight / 2;
        const anchorXRatio = (anchor.x - currentLeft) / currentWidth;
        const anchorYRatio = (anchor.y - currentTop) / currentHeight;
        return {
          zoom: nextZoom,
          centerX: anchor.x - anchorXRatio * nextWidth + nextWidth / 2,
          centerY: anchor.y - anchorYRatio * nextHeight + nextHeight / 2,
        };
      });
    },
    [fineTrackLines, fittedViewport.height, fittedViewport.width],
  );

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !fineTrackLines) return;
    svg.addEventListener('wheel', handlePrecisionWheel, { passive: false });
    return () => svg.removeEventListener('wheel', handlePrecisionWheel);
  }, [fineTrackLines, handlePrecisionWheel]);

  const handlePrecisionPointerDown = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      if (!fineTrackLines || event.button !== 0) return;
      const targetTag = (event.target as SVGElement).tagName.toLowerCase();
      if (targetTag === 'circle' || targetTag === 'text' || targetTag === 'image') return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      panStartRef.current = {
        pointerId: event.pointerId,
        clientX: event.clientX,
        clientY: event.clientY,
        centerX: viewport.centerX,
        centerY: viewport.centerY,
      };
      setPanning(true);
    },
    [fineTrackLines, viewport.centerX, viewport.centerY],
  );

  const handlePrecisionPointerMove = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const start = panStartRef.current;
      if (!start || start.pointerId !== event.pointerId) return;
      event.preventDefault();
      const bounds = event.currentTarget.getBoundingClientRect();
      if (bounds.width <= 0 || bounds.height <= 0) return;
      setViewport((current) => ({
        ...current,
        centerX: start.centerX - ((event.clientX - start.clientX) / bounds.width) * viewWidth,
        centerY: start.centerY - ((event.clientY - start.clientY) / bounds.height) * viewHeight,
      }));
    },
    [viewHeight, viewWidth],
  );

  const finishPrecisionPan = useCallback((event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    panStartRef.current = null;
    setPanning(false);
  }, []);

  const handleSvgClick = useCallback(
    (event: ReactMouseEvent<SVGSVGElement>) => {
      if (!onMapLatLngClick || points.length === 0) {
        return;
      }

      const coord = nearestTrackCoordFromSvgPoint(
        event.currentTarget,
        event.clientX,
        event.clientY,
        points,
      );
      if (coord) {
        onMapLatLngClick(coord);
      }
    },
    [onMapLatLngClick, points],
  );

  useEffect(() => {
    if (!draggingMarkerId || !onMapMarkerDragEnd) {
      return;
    }

    const finishDrag = (clientX: number, clientY: number) => {
      const svg = svgRef.current;
      const markerId = draggingMarkerId;
      setDraggingMarkerId(null);
      if (!svg || !markerId) {
        return;
      }

      const coord = nearestTrackCoordFromSvgPoint(svg, clientX, clientY, points);
      if (coord) {
        onMapMarkerDragEnd(markerId, coord);
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      event.preventDefault();
    };

    const handleMouseUp = (event: MouseEvent) => {
      finishDrag(event.clientX, event.clientY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingMarkerId, onMapMarkerDragEnd, points]);

  const turnMarkersDraggable = Boolean(onMapMarkerDragEnd);
  const draggableMarkerKinds = new Set(['note', 'section-boundary']);
  const hoverPoint = mapHoverMarker ? projectCoordOnTrack(points, mapHoverMarker) : null;
  const projectedHighlightLines = highlightLines.map((line) => ({
    ...line,
    points: line.points
      .map((point) => projectCoordOnTrack(points, point))
      .filter((point): point is TrackMapPoint => point !== null),
  }));

  return (
    <Box
      onClick={onMapClick}
      sx={{
        position: 'relative',
        width: '100%',
        height: fill ? '100%' : height,
        minHeight: fill ? 260 : height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
        px: fill ? 0 : 1,
        py: fill ? 0 : 0,
        borderRadius: title ? '6px' : undefined,
        border: title ? '1px solid rgba(36,62,82,0.78)' : undefined,
        bgcolor: title ? 'rgba(1,13,24,0.46)' : undefined,
        overflow: 'hidden',
      }}
    >
      {title ? (
        <Typography sx={{ flexShrink: 0, px: 0.5, pb: 0.5, fontSize: '0.78rem', fontWeight: 850 }}>
          {title}
        </Typography>
      ) : null}

      {points.length > 1 ? (
        <Box
          sx={{
            flex: fill ? 1 : undefined,
            width: '100%',
            height: fill ? '100%' : 'auto',
            minHeight: fill ? 0 : undefined,
            maxHeight: fill
              ? `calc(100% - ${titleHeight + legendHeight}px)`
              : height - titleHeight - legendHeight - 8,
            display: 'flex',
          }}
        >
          <Box
            component="svg"
            ref={svgRef}
            data-testid="gps-line-svg"
            data-zoom={viewport.zoom.toFixed(3)}
            viewBox={activeViewBox}
            preserveAspectRatio="xMidYMid meet"
            onClick={onMapLatLngClick ? handleSvgClick : undefined}
            onDoubleClick={fineTrackLines ? resetPrecisionViewport : undefined}
            onPointerDown={handlePrecisionPointerDown}
            onPointerMove={handlePrecisionPointerMove}
            onPointerUp={finishPrecisionPan}
            onPointerCancel={finishPrecisionPan}
            onMouseMove={
              onMapLatLngHover
                ? (event) => {
                    const coord = nearestTrackCoordFromSvgPoint(
                      event.currentTarget,
                      event.clientX,
                      event.clientY,
                      points,
                    );
                    onMapLatLngHover(coord);
                  }
                : undefined
            }
            onMouseLeave={onMapLatLngHover ? () => onMapLatLngHover(null) : undefined}
            sx={{
              display: 'block',
              width: '100%',
              height: fill ? '100%' : 'auto',
              minHeight: fill ? 120 : undefined,
              aspectRatio: fill ? undefined : `${VIEWBOX_WIDTH} / ${VIEWBOX_HEIGHT}`,
              touchAction: fineTrackLines ? 'none' : undefined,
              cursor: draggingMarkerId
                ? 'grabbing'
                : panning
                  ? 'grabbing'
                  : fineTrackLines
                    ? 'grab'
                : crosshairCursor || onMapLatLngClick
                  ? 'crosshair'
                  : undefined,
            }}
          >
            <rect
              data-testid="gps-line-background"
              width={VIEWBOX_WIDTH}
              height={VIEWBOX_HEIGHT}
              fill={
                transparentBackground
                  ? 'transparent'
                  : (svgBackgroundColor ?? 'rgba(2,10,18,0.35)')
              }
              rx="8"
            />
            {!hideTrackGraphics
              ? referenceSegments.map((segments, groupIndex) =>
                  segments.map((segment, index) => (
                    <line
                      key={`reference-halo-${groupIndex}-${index}`}
                      x1={segment.x1}
                      y1={segment.y1}
                      x2={segment.x2}
                      y2={segment.y2}
                      stroke="rgba(7,17,28,0.88)"
                      strokeWidth={fineTrackLines ? 3 : 12}
                      strokeLinecap="round"
                    />
                  )),
                )
              : null}
            {!hideTrackGraphics
              ? referenceSegments.map((segments, groupIndex) =>
                  segments.map((segment, index) => (
                    <line
                      key={`reference-line-${groupIndex}-${index}`}
                      x1={segment.x1}
                      y1={segment.y1}
                      x2={segment.x2}
                      y2={segment.y2}
                      stroke="rgba(203,213,225,0.72)"
                      strokeWidth={fineTrackLines ? 1.25 : 5}
                      strokeLinecap="round"
                    />
                  )),
                )
              : null}
            {!hideTrackGraphics && showIdealLine && idealSegments.length > 0
              ? idealSegments.map((segment, index) => (
                  <line
                    key={`ideal-halo-${index}`}
                    x1={segment.x1}
                    y1={segment.y1}
                    x2={segment.x2}
                    y2={segment.y2}
                    stroke="#07111C"
                    strokeWidth={fineTrackLines ? 2.5 : 7}
                    strokeLinecap="round"
                    strokeOpacity={0.82}
                  />
                ))
              : null}
            {!hideTrackGraphics && showIdealLine && idealSegments.length > 0
              ? idealSegments.map((segment, index) => (
                  <line
                    key={`ideal-${index}`}
                    x1={segment.x1}
                    y1={segment.y1}
                    x2={segment.x2}
                    y2={segment.y2}
                    stroke="#38BDF8"
                    strokeWidth={fineTrackLines ? 1.25 : 3}
                    strokeLinecap="round"
                    strokeDasharray="7 5"
                  />
                ))
              : null}
            {showLapLines && !hideTrackGraphics && !fineTrackLines
              ? segmentGroups.map((segments, groupIndex) =>
                  segments.map((segment, index) => (
                    <line
                      key={`halo-${groupIndex}-${index}`}
                      x1={segment.x1}
                      y1={segment.y1}
                      x2={segment.x2}
                      y2={segment.y2}
                      stroke="rgba(37,54,68,0.78)"
                      strokeWidth={fineTrackLines ? 3.5 : 18}
                      strokeLinecap="round"
                    />
                  )),
                )
              : null}
            {showLapLines && !hideTrackGraphics
              ? segmentGroups.map((segments, groupIndex) =>
                  segments.map((segment, index) => (
                    <line
                      key={`speed-${groupIndex}-${index}`}
                      data-testid="gps-speed-line"
                      x1={segment.x1}
                      y1={segment.y1}
                      x2={segment.x2}
                      y2={segment.y2}
                      stroke={segment.color}
                      strokeWidth={fineTrackLines ? 0.35 : 10}
                      strokeLinecap="round"
                    />
                  )),
                )
              : null}
            {!hideTrackGraphics &&
            selectedSectorIndex !== null &&
            sectorHighlightGroups[selectedSectorIndex]?.length
              ? trackSegments(sectorHighlightGroups[selectedSectorIndex]!, speedRange).map(
                  (segment, index) => (
                    <line
                      key={`sector-highlight-${index}`}
                      x1={segment.x1}
                      y1={segment.y1}
                      x2={segment.x2}
                      y2={segment.y2}
                      stroke="#2563EB"
                      strokeWidth={fineTrackLines ? 3.5 : 14}
                      strokeLinecap="round"
                      strokeOpacity={0.95}
                    />
                  ),
                )
              : null}
            {!hideTrackGraphics
              ? channelOverlays.map((overlay, overlayIndex) =>
                  overlay.points.slice(1).flatMap((point, index) => {
                    const previous = overlay.points[index]!;
                    if (!isContinuousTelemetryPair(previous, point)) return [];
                    return [
                      <line
                        key={`channel-overlay-${overlay.id}-${index}`}
                        x1={previous.x}
                        y1={previous.y}
                        x2={point.x}
                        y2={point.y}
                        stroke={point.heatColor}
                        strokeWidth={fineTrackLines ? 1.5 : 6}
                        strokeLinecap="round"
                        strokeOpacity={0.82}
                        style={{ mixBlendMode: overlayIndex === 0 ? 'normal' : 'screen' }}
                      />,
                    ];
                  }),
                )
              : null}
            {!fineTrackLines ? projectedHighlightLines.map((line) =>
              line.points.length >= 2 ? (
                <polyline
                  key={`highlight-${line.id}`}
                  points={line.points.map((point) => `${point.x},${point.y}`).join(' ')}
                  fill="none"
                  stroke={line.color ?? '#F97316'}
                  strokeWidth={(line.width ?? 11) * (fineTrackLines ? 0.3 : 1)}
                  strokeOpacity={line.opacity ?? 0.72}
                  strokeDasharray={line.dash?.join(' ')}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : null,
            ) : null}
            {!hideTrackGraphics && !fineTrackLines && start ? (
              <circle
                cx={start.x}
                cy={start.y}
                r="5"
                fill="#22C55E"
                stroke="#FFFFFF"
                strokeWidth="1"
              />
            ) : null}
            {!hideTrackGraphics && !fineTrackLines && finish ? (
              <circle
                cx={finish.x}
                cy={finish.y}
                r="5"
                fill="#F59E0B"
                stroke="#FFFFFF"
                strokeWidth="1"
              />
            ) : null}
            {!hideTrackGraphics && !fineTrackLines && finish ? (
              <g transform={`translate(${finish.x - 6} ${finish.y - 18})`}>
                <rect width="12" height="12" fill="#FFFFFF" />
                <rect x="0" y="0" width="6" height="6" fill="#020A12" />
                <rect x="6" y="6" width="6" height="6" fill="#020A12" />
              </g>
            ) : null}
            {!hideTrackGraphics && !fineTrackLines && startFinishLine && points.length > 0
              ? (() => {
                  const pointA = projectCoordOnTrack(points, startFinishLine.pointA);
                  const pointB = projectCoordOnTrack(points, startFinishLine.pointB);
                  if (!pointA || !pointB) {
                    return null;
                  }

                  return (
                    <>
                      <line
                        x1={pointA.x}
                        y1={pointA.y}
                        x2={pointB.x}
                        y2={pointB.y}
                        stroke="#FFFFFF"
                        strokeWidth={fineTrackLines ? 5 : 10}
                        strokeLinecap="round"
                        opacity={0.95}
                      />
                      <line
                        x1={pointA.x}
                        y1={pointA.y}
                        x2={pointB.x}
                        y2={pointB.y}
                        stroke="#22C55E"
                        strokeWidth={fineTrackLines ? 3 : 6}
                        strokeLinecap="round"
                      />
                    </>
                  );
                })()
              : null}
            {(crossTrackLines ?? []).map((line) => {
              const pointA = projectCoordOnTrack(points, line.pointA);
              const pointB = projectCoordOnTrack(points, line.pointB);
              if (!pointA || !pointB) {
                return null;
              }

              const midX = (pointA.x + pointB.x) / 2;
              const midY = (pointA.y + pointB.y) / 2;
              const isInteractive = Boolean(line.draggable || line.label || line.selected);
              const isCatalogLine = line.lineStyle === 'catalog';
              const strokeWidth = line.selected ? (isCatalogLine ? 1.5 : 6) : isCatalogLine ? 1 : 5;

              return (
                <g
                  key={line.id}
                  style={{
                    cursor: line.draggable
                      ? draggingMarkerId === line.id
                        ? 'grabbing'
                        : 'grab'
                      : onMapMarkerClick
                        ? 'pointer'
                        : undefined,
                  }}
                  onMouseDown={
                    line.draggable && turnMarkersDraggable
                      ? (event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          onMapMarkerClick?.(line.id);
                          setDraggingMarkerId(line.id);
                        }
                      : undefined
                  }
                  onClick={
                    onMapMarkerClick && !line.draggable
                      ? (event) => {
                          event.stopPropagation();
                          onMapMarkerClick(line.id);
                        }
                      : undefined
                  }
                >
                  {!isCatalogLine ? (
                    <line
                      x1={pointA.x}
                      y1={pointA.y}
                      x2={pointB.x}
                      y2={pointB.y}
                      stroke="#FFFFFF"
                      strokeWidth={strokeWidth + 4}
                      strokeLinecap="round"
                      opacity={0.92}
                    />
                  ) : (
                    <line
                      x1={pointA.x}
                      y1={pointA.y}
                      x2={pointB.x}
                      y2={pointB.y}
                      stroke="rgba(7,17,28,0.4)"
                      strokeWidth={2}
                      strokeLinecap="butt"
                    />
                  )}
                  <line
                    x1={pointA.x}
                    y1={pointA.y}
                    x2={pointB.x}
                    y2={pointB.y}
                    stroke={line.selected ? '#ffe66d' : (line.color ?? '#28c8ff')}
                    strokeWidth={strokeWidth}
                    strokeLinecap={isCatalogLine ? 'butt' : 'round'}
                  />
                  {isInteractive && line.label ? (
                    <text
                      x={midX}
                      y={midY - 10}
                      textAnchor="middle"
                      fill="#FFFFFF"
                      fontSize={11 * renderedMapMarkerScale}
                      fontWeight={800}
                      pointerEvents="none"
                    >
                      {line.label}
                    </text>
                  ) : null}
                </g>
              );
            })}
            {mapMarkers.length > 0
              ? mapMarkers.map((marker) => (
                  <g
                    key={marker.id}
                    style={{
                      cursor:
                        draggableMarkerKinds.has(marker.kind) &&
                        (turnMarkersDraggable || onMapMarkerClick)
                          ? draggingMarkerId === marker.id
                            ? 'grabbing'
                            : 'grab'
                          : undefined,
                    }}
                    onMouseDown={
                      draggableMarkerKinds.has(marker.kind) && turnMarkersDraggable
                        ? (event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            onMapMarkerClick?.(marker.id);
                            setDraggingMarkerId(marker.id);
                          }
                        : undefined
                    }
                    onClick={
                      (draggableMarkerKinds.has(marker.kind) || marker.kind === 'footage-start') &&
                      onMapMarkerClick &&
                      !turnMarkersDraggable
                        ? (event) => {
                            event.stopPropagation();
                            onMapMarkerClick(marker.id);
                          }
                        : undefined
                    }
                  >
                    <circle
                      cx={marker.x}
                      cy={marker.y}
                      r={
                        (marker.kind === 'braking-soft' || marker.kind === 'braking-hard'
                          ? 4.5
                          : marker.kind === 'note'
                            ? 9
                            : marker.kind === 'footage-start'
                              ? 8
                              : marker.selected
                                ? 10
                                : 7) * renderedMapMarkerScale
                      }
                      fill={marker.color}
                      stroke={marker.kind === 'footage-start' ? '#020A12' : '#FFFFFF'}
                      strokeWidth={
                        (marker.kind === 'footage-start' ? 3 : marker.selected ? 2.5 : 2) *
                        renderedMapMarkerScale
                      }
                      opacity={marker.kind === 'sector-boundary' ? 0.95 : 1}
                    />
                    {marker.kind === 'footage-start' ? (
                      <g
                        transform={`translate(${marker.x - 5.9 * renderedMapMarkerScale} ${marker.y - 5.9 * renderedMapMarkerScale}) scale(${renderedMapMarkerScale * 0.5})`}
                        pointerEvents="none"
                      >
                        <path
                          d="M17 10.5V6c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v12c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-4.5l4 4v-11l-4 4ZM15 17H5V7h10v10Z"
                          fill="#020A12"
                        />
                      </g>
                    ) : marker.label ? (
                      <text
                        x={marker.x + 12 * renderedMapMarkerScale}
                        y={marker.y + 4 * renderedMapMarkerScale}
                        fill="#FFFFFF"
                        fontSize={11 * renderedMapMarkerScale}
                        fontWeight="800"
                        stroke="#07111C"
                        strokeWidth={3 * renderedMapMarkerScale}
                        paintOrder="stroke"
                      >
                        {marker.label}
                      </text>
                    ) : null}
                  </g>
                ))
              : null}
            {hoverPoint ? (
              <g pointerEvents="none">
                <circle
                  cx={hoverPoint.x}
                  cy={hoverPoint.y}
                  r={12}
                  fill="rgba(245,158,11,0.28)"
                  stroke="#FFFFFF"
                  strokeWidth={2}
                />
                <circle cx={hoverPoint.x} cy={hoverPoint.y} r={4} fill="#F59E0B" />
                <path
                  d={`M ${hoverPoint.x - 8} ${hoverPoint.y} H ${hoverPoint.x + 8} M ${hoverPoint.x} ${hoverPoint.y - 8} V ${hoverPoint.y + 8}`}
                  stroke="#FFFFFF"
                  strokeWidth={1.5}
                />
              </g>
            ) : null}
            {!hideTrackGraphics ? (
              <SvgReplayCursorLayer
                cursors={activeCursors}
                resolveTimecodeMsRef={resolveReplayCursorTimecodeMsRef}
                compact={fineTrackLines}
                scale={
                  fineTrackLines && followReplayCursor
                    ? replayCursorScale / 3
                    : replayCursorScale
                }
                onFollowPoint={followReplayCursor ? followPrecisionReplayCursor : undefined}
              />
            ) : null}
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', px: 2 }}>
          <Typography
            sx={{ fontSize: '0.85rem', color: '#E2E8F0', fontWeight: 700, textAlign: 'center' }}
          >
            No GPS points for this lap
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: '0.72rem', color: '#94A3B8', textAlign: 'center' }}>
            Try another lap or confirm the session has GPS data.
          </Typography>
        </Box>
      )}

      {mapsDevHint ? (
        <Typography
          sx={{
            flexShrink: 0,
            px: 1,
            py: 0.75,
            fontSize: '0.68rem',
            lineHeight: 1.45,
            color: '#CBD5E1',
            textAlign: 'center',
            borderTop: '1px solid rgba(36,62,82,0.55)',
          }}
        >
          {mapsDevHint}
        </Typography>
      ) : null}

      {showLegend ? (
        <TrackMapLegend trackItems={legendTrackItems} showIdealLine={showIdealLine} />
      ) : null}
    </Box>
  );
}

export function GpsSpeedTrackMap(props: GpsSpeedTrackMapProps) {
  const {
    fill = false,
    height = 292,
    basemapProvider = 'google',
    showBasemap = true,
  } = props;
  const [internalBrakingOverlayEnabled, setInternalBrakingOverlayEnabled] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('paddiq:map:braking-overlay') !== 'false';
  });
  const [overlayMenuAnchor, setOverlayMenuAnchor] = useState<HTMLElement | null>(null);
  const [internalCoastingOverlayEnabled, setInternalCoastingOverlayEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('paddiq:map:coasting-overlay') === 'true';
  });
  const brakingOverlayEnabled = props.brakingOverlayEnabled ?? internalBrakingOverlayEnabled;
  const coastingOverlayEnabled = props.coastingOverlayEnabled ?? internalCoastingOverlayEnabled;
  const setBrakingOverlayEnabled = useCallback(
    (enabled: boolean) => {
      setInternalBrakingOverlayEnabled(enabled);
      props.onBrakingOverlayEnabledChange?.(enabled);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('paddiq:map:braking-overlay', String(enabled));
      }
    },
    [props.onBrakingOverlayEnabledChange],
  );
  const setCoastingOverlayEnabled = useCallback(
    (enabled: boolean) => {
      setInternalCoastingOverlayEnabled(enabled);
      props.onCoastingOverlayEnabledChange?.(enabled);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('paddiq:map:coasting-overlay', String(enabled));
      }
    },
    [props.onCoastingOverlayEnabledChange],
  );
  const brakingZones = useMemo(
    () =>
      props.showBrakingSpots && brakingOverlayEnabled ? analyzeBrakingZones(props.points) : [],
    [brakingOverlayEnabled, props.points, props.showBrakingSpots],
  );
  const coastingZones = useMemo(
    () =>
      props.showCoastingSpots && coastingOverlayEnabled ? analyzeCoastingZones(props.points) : [],
    [coastingOverlayEnabled, props.points, props.showCoastingSpots],
  );
  const mapProps: GpsSpeedTrackMapProps = {
    ...props,
    mapMarkers: [
      ...(props.mapMarkers ?? []),
      ...brakingZones.map((zone) => ({
        id: `${zone.id}-peak`,
        kind: zone.severity === 'hard' ? ('braking-hard' as const) : ('braking-soft' as const),
        label: '',
        lat: zone.peakPoint.lat,
        lon: zone.peakPoint.lon,
        x: zone.peakPoint.x,
        y: zone.peakPoint.y,
        color: zone.severity === 'hard' ? '#EF4444' : '#FBBF24',
      })),
    ],
    highlightLines: [
      ...(props.highlightLines ?? []),
      ...coastingZones.flatMap((zone) => {
        const points = smoothTelemetryArc(zone.points);
        return [
          {
            id: `${zone.id}-halo`,
            points,
            color: '#07111C',
            width: 8,
            opacity: 0.62,
          },
          {
            id: `${zone.id}-line`,
            points,
            color: '#38BDF8',
            width: 4.5,
            opacity: 0.94,
          },
        ];
      }),
      ...brakingZones.flatMap((zone) => {
        const points = smoothTelemetryArc(zone.points);
        return [
          {
            id: `${zone.id}-halo`,
            points,
            color: '#07111C',
            width: 9,
            opacity: 0.72,
          },
          {
            id: `${zone.id}-line`,
            points,
            color: zone.severity === 'hard' ? '#EF4444' : '#FBBF24',
            width: zone.severity === 'hard' ? 6 : 5,
            opacity: 0.96,
          },
        ];
      }),
    ],
  };
  const apiKey = getGoogleMapsApiKey();
  const mapboxToken = getMapboxAccessToken();
  const canUseMap = Boolean(hasGeoPoints(props.points) || props.mapViewport);
  const preferMapbox = showBasemap && basemapProvider === 'mapbox' && canUseMap;
  const preferAppleMaps = showBasemap && basemapProvider === 'apple' && canUseMap;
  const [mapboxFailed, setMapboxFailed] = useState(false);
  const [mapboxTilesReady, setMapboxTilesReady] = useState(false);
  const [appleMapsReady, setAppleMapsReady] = useState(false);
  const [appleMapsFailed, setAppleMapsFailed] = useState(false);
  const [appleTilesReady, setAppleTilesReady] = useState(false);
  const canUseGoogleMaps = Boolean(
    showBasemap &&
    apiKey &&
    canUseMap &&
    (basemapProvider === 'google' ||
      (preferMapbox && (!mapboxToken || mapboxFailed)) ||
      (preferAppleMaps && appleMapsFailed)),
  );
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const [googleTilesReady, setGoogleTilesReady] = useState(false);
  const [mapsAuthFailed, setMapsAuthFailed] = useState(mapsAuthHasFailed());

  useEffect(() => {
    if (!preferMapbox) {
      setMapboxFailed(false);
      setMapboxTilesReady(false);
    }
  }, [preferMapbox]);

  useEffect(() => {
    if (!preferAppleMaps) {
      setAppleMapsReady(false);
      setAppleMapsFailed(false);
      setAppleTilesReady(false);
      return;
    }

    let cancelled = false;
    void loadMapKit()
      .then(() => {
        if (!cancelled && !mapkitAuthHasFailed()) {
          setAppleMapsFailed(false);
          setAppleMapsReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAppleMapsReady(false);
          setAppleMapsFailed(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [preferAppleMaps]);

  useEffect(() => {
    registerGoogleMapsAuthFailureHandler();
    if (import.meta.env.DEV) {
      resetMapsAuthFailure();
      setMapsAuthFailed(false);
    }
    return onMapsAuthFailure(() => {
      setMapsAuthFailed(true);
      setGoogleMapsReady(false);
      setGoogleTilesReady(false);
    });
  }, []);

  useEffect(() => {
    if (!apiKey || !canUseGoogleMaps) {
      setGoogleMapsReady(false);
      setGoogleTilesReady(false);
      return;
    }

    let cancelled = false;
    void loadGoogleMaps(apiKey)
      .then(() => {
        if (!cancelled && !mapsAuthHasFailed()) {
          setMapsAuthFailed(false);
          setGoogleMapsReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setGoogleMapsReady(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, canUseGoogleMaps]);

  const showMapboxUnderlay = Boolean(preferMapbox && mapboxToken && !mapboxFailed);
  const showAppleUnderlay = Boolean(preferAppleMaps && appleMapsReady && !appleMapsFailed);
  const showGoogleUnderlay = Boolean(
    apiKey && canUseGoogleMaps && googleMapsReady && !mapsAuthFailed,
  );
  const activeUnderlayReady = showMapboxUnderlay
    ? mapboxTilesReady
    : showAppleUnderlay
      ? appleTilesReady
      : showGoogleUnderlay
        ? googleTilesReady
        : false;
  const useSvgFallback =
    !(showMapboxUnderlay || showAppleUnderlay || showGoogleUnderlay) || !activeUnderlayReady;
  const svgMapProps =
    showMapboxUnderlay || showAppleUnderlay || showGoogleUnderlay
      ? {
          ...mapProps,
          referenceTrackGroups: [],
          trackGroups: [],
          idealPoints: [],
          showIdealLine: false,
          showLapLines: false,
          channelOverlays: [],
          mapMarkers: [],
          startFinishLine: undefined,
        }
      : mapProps;

  const handleGoogleTilesReady = useCallback(() => {
    resetMapsAuthFailure();
    setMapsAuthFailed(false);
    setGoogleTilesReady(true);
  }, []);

  const handleAppleTilesReady = useCallback(() => {
    setAppleTilesReady(true);
  }, []);

  const handleMapboxTilesReady = useCallback(() => {
    setMapboxTilesReady(true);
  }, []);

  const handleMapboxLoadError = useCallback(() => {
    setMapboxFailed(true);
    setMapboxTilesReady(false);
  }, []);

  const mapsDevHint =
    mapsAuthFailed && !googleTilesReady && typeof window !== 'undefined'
      ? mapsDevAuthHint(apiKey?.slice(-6))
      : null;
  const mapsMissingKey =
    !apiKey && !showMapboxUnderlay && !showAppleUnderlay && typeof window !== 'undefined'
      ? mapsMissingKeyHint()
      : null;
  const mapboxMissingKey =
    preferMapbox && !mapboxToken && typeof window !== 'undefined' ? mapboxMissingKeyHint() : null;
  const appleMapsHint =
    preferAppleMaps && appleMapsFailed && !canUseGoogleMaps && typeof window !== 'undefined'
      ? mapkitMissingConfigHint()
      : null;
  const basemapStillLoading =
    (preferMapbox && Boolean(mapboxToken) && !mapboxFailed && !mapboxTilesReady) ||
    (preferAppleMaps && !appleMapsReady && !appleMapsFailed);
  const basemapUnavailable =
    !basemapStillLoading && !showMapboxUnderlay && !showAppleUnderlay && !showGoogleUnderlay;
  const mapsBlockingMessage = showBasemap && basemapUnavailable
    ? (mapboxMissingKey ?? appleMapsHint ?? mapsDevHint ?? mapsMissingKey)
    : null;

  return (
    <Box
      data-testid="gps-track-map"
      data-basemap={showBasemap ? 'on' : 'off'}
      sx={{
        position: 'relative',
        width: '100%',
        height: fill ? '100%' : height,
        minHeight: fill ? 260 : height,
      }}
    >
      {mapsBlockingMessage ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 6,
            display: 'grid',
            placeItems: 'center',
            px: 2.5,
            textAlign: 'center',
            bgcolor: 'rgba(2,10,18,0.92)',
          }}
        >
          <Box sx={{ maxWidth: 520 }}>
            <Typography sx={{ fontSize: '0.92rem', color: '#F8FAFC', fontWeight: 800, mb: 0.75 }}>
              {showMapboxUnderlay || preferMapbox
                ? 'Mapbox could not load'
                : showAppleUnderlay || preferAppleMaps
                  ? 'Apple Maps could not load'
                  : 'Google Maps could not load'}
            </Typography>
            <Typography sx={{ fontSize: '0.76rem', color: '#94A3B8', lineHeight: 1.55 }}>
              {mapsBlockingMessage}
            </Typography>
          </Box>
        </Box>
      ) : null}
      {showMapboxUnderlay ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            width: '100%',
            height: '100%',
            opacity: mapboxTilesReady ? 1 : 0,
            pointerEvents: mapboxTilesReady ? 'auto' : 'none',
            transition: 'opacity 180ms ease',
          }}
        >
          <MapboxTrackMap
            accessToken={mapboxToken!}
            {...mapProps}
            onTilesReady={handleMapboxTilesReady}
            onLoadError={handleMapboxLoadError}
          />
        </Box>
      ) : null}
      {showAppleUnderlay ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            width: '100%',
            height: '100%',
            opacity: appleTilesReady ? 1 : 0,
            pointerEvents: appleTilesReady ? 'auto' : 'none',
            transition: 'opacity 180ms ease',
          }}
        >
          <AppleMapsTrackMap {...mapProps} onTilesReady={handleAppleTilesReady} />
        </Box>
      ) : null}
      {showGoogleUnderlay ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            width: '100%',
            height: '100%',
            opacity: googleTilesReady ? 1 : 0,
            pointerEvents: googleTilesReady ? 'auto' : 'none',
            transition: 'opacity 180ms ease',
          }}
        >
          <GoogleMapsTrackMap
            apiKey={apiKey!}
            {...mapProps}
            onTilesReady={handleGoogleTilesReady}
          />
        </Box>
      ) : null}
      {useSvgFallback ? (
        <Box
          data-testid="gps-line-canvas"
          sx={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            height: '100%',
            bgcolor: showBasemap ? undefined : '#000000',
          }}
        >
          <SvgGpsSpeedTrackMap
            {...svgMapProps}
            mapsDevHint={mapsDevHint}
            transparentBackground={showMapboxUnderlay || showAppleUnderlay || showGoogleUnderlay}
            svgBackgroundColor={showBasemap ? props.svgBackgroundColor : '#000000'}
            fineTrackLines={!showBasemap}
            hideTrackGraphics={showMapboxUnderlay || showAppleUnderlay || showGoogleUnderlay}
          />
        </Box>
      ) : null}
      {brakingZones.length > 0 || coastingZones.length > 0 ? (
        <Box
          sx={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            zIndex: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1,
            py: 0.65,
            borderRadius: 1,
            bgcolor: 'rgba(2,10,18,0.86)',
            border: '1px solid rgba(148,163,184,0.34)',
            pointerEvents: 'none',
          }}
        >
          {coastingZones.length > 0 ? (
            <>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#38BDF8' }} />
              <Typography sx={{ fontSize: '0.6rem', color: '#E2E8F0', fontWeight: 700 }}>
                Estimated coast
              </Typography>
            </>
          ) : null}
          {brakingZones.length > 0 ? (
            <>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  ml: coastingZones.length > 0 ? 0.4 : 0,
                  borderRadius: '50%',
                  bgcolor: '#FBBF24',
                }}
              />
              <Typography sx={{ fontSize: '0.6rem', color: '#E2E8F0', fontWeight: 700 }}>
                Soft
              </Typography>
              <Box sx={{ width: 7, height: 7, ml: 0.4, borderRadius: '50%', bgcolor: '#EF4444' }} />
              <Typography sx={{ fontSize: '0.6rem', color: '#E2E8F0', fontWeight: 700 }}>
                Hard braking
              </Typography>
            </>
          ) : null}
        </Box>
      ) : null}
      {(props.showBrakingSpots || props.showCoastingSpots) && props.showBrakingToggle !== false ? (
        <>
          <Box
            component="button"
            type="button"
            onClick={(event) => setOverlayMenuAnchor(event.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={Boolean(overlayMenuAnchor)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 56,
              zIndex: 9,
              display: 'flex',
              alignItems: 'center',
              gap: 0.65,
              px: 1.1,
              py: 0.7,
              borderRadius: 1,
              color: '#FFFFFF',
              bgcolor: 'rgba(2,10,18,0.88)',
              border: '1px solid rgba(148,163,184,0.42)',
              boxShadow: '0 8px 22px rgba(0,0,0,0.3)',
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            <LayersOutlinedIcon sx={{ fontSize: 16, color: '#C4B5FD' }} />
            <Typography
              component="span"
              sx={{ fontSize: '0.66rem', color: 'inherit', fontWeight: 800 }}
            >
              Overlays
            </Typography>
          </Box>
          <Menu
            anchorEl={overlayMenuAnchor}
            open={Boolean(overlayMenuAnchor)}
            onClose={() => setOverlayMenuAnchor(null)}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.5,
                  minWidth: 230,
                  bgcolor: 'rgba(2,10,18,0.97)',
                  color: '#F8FAFC',
                  border: '1px solid rgba(148,163,184,0.28)',
                  boxShadow: '0 16px 36px rgba(0,0,0,0.46)',
                },
              },
            }}
          >
            {props.showBrakingSpots ? (
              <MenuItem
                onClick={() => setBrakingOverlayEnabled(!brakingOverlayEnabled)}
                sx={{ minHeight: 48, gap: 1 }}
              >
                <LocalFireDepartmentOutlinedIcon
                  sx={{ fontSize: 18, color: brakingOverlayEnabled ? '#FBBF24' : '#64748B' }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.76rem', fontWeight: 850 }}>
                    Braking zones
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#94A3B8' }}>
                    Soft and hard deceleration
                  </Typography>
                </Box>
                <Switch size="small" checked={brakingOverlayEnabled} tabIndex={-1} />
              </MenuItem>
            ) : null}
            {props.showCoastingSpots ? (
              <MenuItem
                onClick={() => setCoastingOverlayEnabled(!coastingOverlayEnabled)}
                sx={{ minHeight: 48, gap: 1 }}
              >
                <AirOutlinedIcon
                  sx={{ fontSize: 18, color: coastingOverlayEnabled ? '#38BDF8' : '#64748B' }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.76rem', fontWeight: 850 }}>
                    Estimated coasting
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#94A3B8' }}>
                    Sustained mild deceleration
                  </Typography>
                </Box>
                <Switch size="small" checked={coastingOverlayEnabled} tabIndex={-1} />
              </MenuItem>
            ) : null}
          </Menu>
        </>
      ) : null}
    </Box>
  );
}
